import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SSEAlert, AlertType } from '../contexts/SSEContext';
import { useSSE } from '../hooks/useSSE';
import { usePermission } from '../hooks/usePermission';

const TAB_CONFIG: { label: string; type: AlertType | 'all'; emoji: string }[] = [
  { label: '전체', type: 'all', emoji: '' },
  { label: '과속', type: 'speeding', emoji: '🔴' },
  { label: '회사', type: 'company_pending', emoji: '🔵' },
  { label: '시스템', type: 'system', emoji: '⚠️' },
];

function tabsForRole(role: string | null) {
  if (role === 'super_admin') return TAB_CONFIG;
  if (role === 'admin') return TAB_CONFIG.filter((t) => t.type !== 'system');
  return TAB_CONFIG.filter((t) => t.type === 'all' || t.type === 'speeding');
}

const BellDropdown: React.FC = () => {
  const { alerts, unreadCount, dismissAlert, deleteAlert } = useSSE();
  const { role } = usePermission();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<AlertType | 'all'>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const tabs = tabsForRole(role);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = tab === 'all' ? alerts : alerts.filter((a) => a.type === tab);
  const display = filtered.slice(0, 10);
  const hasMore = filtered.length > 10;

  const handleClick = (alert: SSEAlert) => {
    dismissAlert(alert.id);
    if (alert.type === 'company_pending') {
      const companyId = alert.data?.company_id;
      navigate(companyId ? `/companies?highlight=${companyId}` : '/companies');
    } else if (alert.type === 'system' && alert.data?.download_url) {
      window.open(alert.data.download_url as string, '_blank');
    }
  };

  const alertColor = (type: AlertType) => {
    if (type === 'speeding') return '#F44336';
    if (type === 'company_pending') return '#2196F3';
    return '#FF9800';
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className={`toolbar__bell ${unreadCount > 0 ? 'toolbar__bell--shake' : ''}`}
        onClick={() => setOpen(!open)}
        title={`알림 ${unreadCount}건`}
      >
        🔔
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              background: '#F44336',
              color: '#fff',
              borderRadius: '50%',
              fontSize: 10,
              width: 18,
              height: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: 8,
            width: 340,
            maxHeight: 420,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            zIndex: 1000,
          }}
        >
          <div style={{ display: 'flex', borderBottom: '1px solid #eee' }}>
            {tabs.map((t) => (
              <button
                key={t.type}
                type="button"
                onClick={() => setTab(t.type)}
                style={{
                  flex: 1,
                  padding: '8px 4px',
                  border: 'none',
                  background: tab === t.type ? '#f0f0f0' : 'transparent',
                  fontWeight: tab === t.type ? 700 : 400,
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                {t.emoji} {t.label}
              </button>
            ))}
          </div>

          {display.length === 0 ? (
            <p style={{ padding: 16, textAlign: 'center', color: '#999', fontSize: 13 }}>
              알림이 없습니다
            </p>
          ) : (
            display.map((alert) => (
              <div
                key={alert.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderBottom: '1px solid #f5f5f5',
                  background: alert.dismissed ? '#fafafa' : '#fff',
                  cursor: 'pointer',
                }}
                onClick={() => handleClick(alert)}
              >
                <div style={{ flex: 1 }}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: alert.dismissed ? '#ccc' : alertColor(alert.type),
                      marginRight: 8,
                    }}
                  />
                  <span style={{ fontSize: 13, color: alert.dismissed ? '#999' : '#333' }}>
                    {alert.title}
                  </span>
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 2, marginLeft: 16 }}>
                    {new Date(alert.timestamp).toLocaleTimeString()}
                  </div>
                </div>
                {alert.dismissed && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteAlert(alert.id);
                    }}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: '#999',
                      cursor: 'pointer',
                      fontSize: 16,
                      padding: '0 4px',
                    }}
                    title="삭제"
                  >
                    ×
                  </button>
                )}
              </div>
            ))
          )}

          {hasMore && (
            <div style={{ textAlign: 'center', padding: 8 }}>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate('/audit-log');
                }}
                style={{
                  border: 'none',
                  background: 'none',
                  color: '#4CAF50',
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                더 보기
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BellDropdown;
